// Loon history table for the library dialog

import React, { useEffect } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/utilities/cn';
import { useProductHistory } from '../../../hooks';
import {
  findGameName,
  formatProductLoonTransactionDate,
  formatProductLoonTransactionDelta,
  getProductLoonTransactionLabel,
} from '@airhead-games/shared/utilities';
import type { UserLoonTransaction } from '../../../transport/api/protectedApi';

const LOON_HISTORY_LIMIT = 50;
const LOON_HISTORY_SKELETON_ROWS = Array.from(
  { length: 6 },
  (_, index) => index
);

interface LoonHistoryTableProps {
  refreshKey?: number | string;
}

const getTransactionTypeDisplay = (
  transaction: UserLoonTransaction
): { label: string; detail: string | null } => {
  const label = getProductLoonTransactionLabel(transaction.type);

  if (transaction.type === 'gameStart' && transaction.gameId) {
    return { label, detail: findGameName(transaction.gameId) ?? null };
  }

  if (transaction.type === 'achievement' && transaction.achievement) {
    const { title, level } = transaction.achievement;
    return { label, detail: level === null ? title : `${title} ${level}` };
  }

  return { label, detail: null };
};

const LoonHistoryTable: React.FC<LoonHistoryTableProps> = ({ refreshKey }) => {
  const {
    transactions,
    nextCursor,
    loading,
    loadingMore,
    error,
    refetch,
    loadMore,
  } = useProductHistory({
    autoFetch: false,
    limit: LOON_HISTORY_LIMIT,
  });

  useEffect(() => {
    void refetch();
  }, [refreshKey, refetch]);

  const showInitialLoading = loading && transactions.length === 0;
  const showInitialError = !!error && transactions.length === 0;

  return (
    <div className="min-h-0">
      {showInitialError ? (
        <Alert variant="destructive">
          <AlertTitle>Unable to load loon history</AlertTitle>
          <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span>{error}</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                void refetch();
              }}
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      ) : (
        <div className="w-fit max-w-full min-h-0 overflow-hidden rounded-xl border bg-card">
          <Table
            containerClassName="max-h-[60vh] overflow-auto"
            className="min-w-[34rem] w-max"
          >
            <TableCaption className="sr-only">
              A list of recent loon balance changes.
            </TableCaption>
            <TableHeader className="sticky top-0 z-20 bg-card">
              <TableRow className="hover:bg-transparent">
                <TableHead className="sticky top-0 z-20 w-[9rem] bg-card px-3 shadow-[inset_0_-1px_0_0_var(--color-border)]">
                  Date
                </TableHead>
                <TableHead className="sticky top-0 z-20 bg-card px-3 shadow-[inset_0_-1px_0_0_var(--color-border)]">
                  Type
                </TableHead>
                <TableHead className="sticky top-0 z-20 w-[6rem] bg-card px-3 text-right shadow-[inset_0_-1px_0_0_var(--color-border)]">
                  Amount
                </TableHead>
                <TableHead className="sticky top-0 z-20 w-[6.75rem] bg-card px-3 text-right shadow-[inset_0_-1px_0_0_var(--color-border)]">
                  Balance
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {showInitialLoading ? (
                LOON_HISTORY_SKELETON_ROWS.map((rowIndex) => (
                  <TableRow key={rowIndex}>
                    <TableCell className="px-3">
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                    <TableCell className="px-3">
                      <div className="flex items-center gap-2">
                        <Skeleton className="h-4 w-20" />
                        <Skeleton className="h-4 w-28" />
                      </div>
                    </TableCell>
                    <TableCell className="px-3">
                      <div className="flex justify-end">
                        <Skeleton className="h-4 w-20" />
                      </div>
                    </TableCell>
                    <TableCell className="px-3 text-right">
                      <div className="flex justify-end">
                        <Skeleton className="h-4 w-20" />
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : transactions.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    className="h-24 px-3 text-center text-muted-foreground"
                  >
                    No loon transactions yet.
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((transaction) => {
                  const { label, detail } =
                    getTransactionTypeDisplay(transaction);
                  const deltaIsNegative = transaction.delta < 0;

                  return (
                    <TableRow key={transaction._id}>
                      <TableCell className="px-3 whitespace-nowrap text-muted-foreground">
                        {formatProductLoonTransactionDate(
                          transaction.createdAt
                        )}
                      </TableCell>
                      <TableCell className="min-w-0 px-3">
                        {/* The dash is its own item so the gap spaces it
                            evenly on both sides. */}
                        <div className="flex min-w-0 items-center gap-1">
                          <span className="shrink-0 font-medium">{label}</span>
                          {detail && (
                            <>
                              <span className="text-muted-foreground">-</span>
                              <span className="max-w-[12rem] truncate text-muted-foreground">
                                {detail}
                              </span>
                            </>
                          )}
                        </div>
                      </TableCell>
                      <TableCell
                        className={cn(
                          'px-3 text-right font-medium tabular-nums',
                          deltaIsNegative ? 'text-destructive' : 'text-primary'
                        )}
                      >
                        {formatProductLoonTransactionDelta(transaction.delta)}
                      </TableCell>
                      <TableCell className="px-3 text-right font-medium tabular-nums">
                        {transaction.balanceAfter.toLocaleString('en-US')}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          {nextCursor && (
            <div className="flex justify-end border-t bg-muted/20 px-4 py-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  void loadMore();
                }}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading...' : 'Load older'}
              </Button>
            </div>
          )}
        </div>
      )}

      {error && transactions.length > 0 && (
        <Alert variant="destructive">
          <AlertTitle>Couldn&apos;t load older transactions</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default LoonHistoryTable;
